import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { businessesKey, businessKey } from "./query";
import type {
  QuickbooksAccount,
  QuickbooksConnection,
  QuickbooksConnectionDetails,
  QuickbooksConnectionUpdate,
  QuickbooksVendor,
} from "./quickbooks";
import { isQuickbooksSyncing } from "./quickbooks-utils";

interface QuickbooksAuthorizeResponse {
  authorize_url: string;
}

/** While the account mirror is still empty after connecting, poll this often. */
export const QUICKBOOKS_SYNC_POLL_MS = 3000;

/** Everything QuickBooks for one business, so a reconnect can refresh it all. */
export const quickbooksKey = (businessId?: string | null) =>
  ["quickbooks", businessId] as const;
export const quickbooksConnectionKey = (businessId?: string | null) =>
  [...quickbooksKey(businessId), "connection"] as const;
export const quickbooksAccountsKey = (businessId?: string | null) =>
  [...quickbooksKey(businessId), "accounts"] as const;
/**
 * The last segment of an invoice's push-status key (`invoiceQuickbooksBillKey`
 * in Accounts Payable), here so a QuickBooks change can refresh it without
 * importing Accounts Payable's keys.
 */
export const QUICKBOOKS_BILL_PUSH_STATUS = "quickbooks-bill";

/**
 * Whether an invoice can be pushed rides on the connection, its settings and
 * the vendor's account, so changing any of them refreshes every open
 * invoice's push status instead of leaving its button stale.
 */
export const invalidateQuickbooksBillPushStatuses = (
  queryClient: QueryClient,
  businessId?: string | null,
) =>
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[1] === businessId &&
      queryKey.at(-1) === QUICKBOOKS_BILL_PUSH_STATUS,
  });

export const quickbooksVendorSearchKey = (
  businessId: string | null | undefined,
  q: string,
) => [...quickbooksKey(businessId), "vendors", q] as const;

export const useStartQuickbooksOauth = (businessId?: string | null) => {
  const api = useApi();

  return useMutation({
    mutationFn: (): Promise<QuickbooksAuthorizeResponse> => {
      if (!businessId) {
        return Promise.reject(new Error("Business not found."));
      }

      return api
        .post(`/businesses/${businessId}/quickbooks/oauth/authorize`)
        .then((response) => response.data);
    },
  });
};

/**
 * The business's QuickBooks connection and bill settings. Readable by any
 * business user. With `pollWhileSyncing`, refetches until the account mirror
 * has rows, which happens in the background right after connecting.
 */
export const useGetQuickbooksConnection = (
  businessId?: string | null,
  { enabled = true, pollWhileSyncing = false } = {},
) => {
  const api = useApi();

  return useQuery({
    queryKey: quickbooksConnectionKey(businessId),
    queryFn: () =>
      api
        .get<QuickbooksConnection>(
          `/businesses/${businessId}/quickbooks/connection`,
        )
        .then((response) => response.data),
    enabled: !!businessId && enabled,
    refetchInterval: (query) =>
      pollWhileSyncing && isQuickbooksSyncing(query.state.data)
        ? QUICKBOOKS_SYNC_POLL_MS
        : false,
  });
};

/** Active QuickBooks expense accounts, sorted by the API. */
export const useGetQuickbooksAccounts = (
  businessId?: string | null,
  { enabled = true } = {},
) => {
  const api = useApi();

  return useQuery({
    queryKey: quickbooksAccountsKey(businessId),
    queryFn: () =>
      api
        .get<
          QuickbooksAccount[]
        >(`/businesses/${businessId}/quickbooks/accounts`)
        .then((response) => response.data),
    enabled: !!businessId && enabled,
  });
};

/**
 * QuickBooks vendors whose display or company name contains `query`. A blank
 * query lists the first 250 by name, so the picker can be browsed.
 */
export const useSearchQuickbooksVendors = (
  businessId?: string | null,
  query = "",
  { enabled = true } = {},
) => {
  const api = useApi();
  const q = query.trim();

  return useQuery({
    queryKey: quickbooksVendorSearchKey(businessId, q),
    queryFn: ({ signal }) =>
      api
        .get<
          QuickbooksVendor[]
        >(`/businesses/${businessId}/quickbooks/vendors`, { params: q ? { q } : undefined, signal })
        .then((response) => response.data),
    enabled: !!businessId && enabled,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * PATCH bill settings. Rejects with the API's 422 `{ errors }` body intact.
 * The business rides in the variables, not the hook, so a save still lands in
 * its own business's cache when the selected business changes mid-request.
 */
export const useUpdateQuickbooksConnection = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      businessId,
      changes,
    }: {
      businessId: string;
      changes: QuickbooksConnectionUpdate;
    }) =>
      api
        .patch<QuickbooksConnectionDetails>(
          `/businesses/${businessId}/quickbooks/connection`,
          changes,
        )
        .then((response) => response.data),
    onSuccess: async (connection, { businessId }) => {
      queryClient.setQueryData(quickbooksConnectionKey(businessId), connection);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: quickbooksConnectionKey(businessId),
        }),
        invalidateQuickbooksBillPushStatuses(queryClient, businessId),
      ]);
    },
  });
};

/** Disconnect keeps settings, mirrors and vendor links for a reconnect. */
export const useDisconnectQuickbooks = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.delete(`/businesses/${businessId}/quickbooks/oauth`).then(() => {}),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: quickbooksKey(businessId) }),
        queryClient.invalidateQueries({ queryKey: businessKey(businessId) }),
        queryClient.invalidateQueries({ queryKey: businessesKey }),
        invalidateQuickbooksBillPushStatuses(queryClient, businessId),
      ]);
    },
  });
};
